<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFormsStore } from 'dashboard/stores/forms';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import EmbedCodeDialog from 'dashboard/components-next/FormBuilder/EmbedCodeDialog.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const formsStore = useFormsStore();

const embedDialogRef = ref(null);
const deleteDialogRef = ref(null);
const formToDelete = ref(null);

const forms = computed(() => formsStore.records);
const isFetching = computed(() => formsStore.uiFlags.fetchingList);

const goTo = (name, params = {}) =>
  router.push(accountScopedRoute(name, params));

const askDelete = form => {
  formToDelete.value = form;
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  try {
    await formsStore.delete(formToDelete.value.id);
    useAlert(t('FORM_BUILDER.DELETE.SUCCESS'));
  } catch {
    useAlert(t('FORM_BUILDER.DELETE.ERROR'));
  }
  deleteDialogRef.value?.close();
};

onMounted(() => formsStore.get());
</script>

<template>
  <SettingsLayout>
    <BaseSettingsHeader
      :title="t('FORM_BUILDER.TITLE')"
      :description="t('FORM_BUILDER.DESCRIPTION')"
    >
      <template #actions>
        <Button
          icon="i-lucide-plus"
          :label="t('FORM_BUILDER.NEW')"
          @click="goTo('forms_settings_new')"
        />
      </template>
    </BaseSettingsHeader>

    <div class="flex flex-col gap-3 mt-6 max-w-4xl">
      <p v-if="isFetching" class="text-sm text-n-slate-10">
        {{ t('FORM_BUILDER.LOADING') }}
      </p>
      <p v-else-if="!forms.length" class="text-sm text-n-slate-10">
        {{ t('FORM_BUILDER.EMPTY') }}
      </p>

      <div
        v-for="form in forms"
        :key="form.id"
        class="flex flex-wrap items-center gap-4 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
      >
        <div class="flex flex-col flex-1 min-w-48 gap-1">
          <span class="font-medium text-n-slate-12">{{ form.name }}</span>
          <span class="text-xs text-n-slate-11">
            {{ t('FORM_BUILDER.COLUMNS.SUBMISSIONS') }}:
            {{ form.submissionsLast30Days }}
          </span>
        </div>
        <span
          class="px-2 py-0.5 text-xs font-medium rounded-full"
          :class="
            form.status === 'active'
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
        >
          {{ t(`FORM_BUILDER.STATUS.${form.status.toUpperCase()}`) }}
        </span>
        <div class="flex items-center gap-1">
          <Button
            sm
            faded
            slate
            icon="i-lucide-code"
            :label="t('FORM_BUILDER.ACTIONS.COPY_CODE')"
            @click="embedDialogRef?.open(form.publicKey)"
          />
          <Button
            sm
            faded
            slate
            icon="i-lucide-inbox"
            :label="t('FORM_BUILDER.ACTIONS.SUBMISSIONS')"
            @click="goTo('forms_settings_submissions', { formId: form.id })"
          />
          <Button
            sm
            faded
            slate
            icon="i-lucide-pencil"
            :label="t('FORM_BUILDER.ACTIONS.EDIT')"
            @click="goTo('forms_settings_edit', { formId: form.id })"
          />
          <Button
            sm
            faded
            ruby
            icon="i-lucide-trash-2"
            :title="t('FORM_BUILDER.ACTIONS.DELETE')"
            @click="askDelete(form)"
          />
        </div>
      </div>
    </div>

    <EmbedCodeDialog ref="embedDialogRef" />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('FORM_BUILDER.DELETE.TITLE')"
      :description="t('FORM_BUILDER.DELETE.MESSAGE')"
      :confirm-button-label="t('FORM_BUILDER.ACTIONS.DELETE')"
      @confirm="confirmDelete"
    />
  </SettingsLayout>
</template>
